import { t } from "@lingui/macro";
import {
  CaretUp,
  Check,
  CreditCard,
  FileText,
  Gear,
  Globe,
  MagnifyingGlass,
  Question,
  SignOut,
  Star,
  UserCircle,
} from "@phosphor-icons/react";
import { languages } from "@stylingcv/utils";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { changeLanguage } from "@/client/providers/locale";
import { useLogout } from "@/client/services/auth";
import { useAuthStore } from "@/client/stores/auth";
import { getCurrentUrlLocale } from "@/client/utils/url-locales";
import { isRTLLocale } from "@/client/utils/rtl-helpers";

/* ── Language flag mapping ─────────────────────────────────────────────────── */
const LANGUAGE_FLAGS: Record<string, string> = {
  "af-ZA": "za", "am-ET": "et", "ar-SA": "sa", "bg-BG": "bg", "bn-BD": "bd",
  "ca-ES": "es", "cs-CZ": "cz", "da-DK": "dk", "de-DE": "de", "el-GR": "gr",
  "en-US": "gb", "es-ES": "es", "fa-IR": "ir", "fi-FI": "fi", "fr-FR": "fr",
  "he-IL": "il", "hi-IN": "in", "hu-HU": "hu", "id-ID": "id", "it-IT": "it",
  "ja-JP": "jp", "km-KH": "kh", "kn-IN": "in", "ko-KR": "kr", "lt-LT": "lt",
  "lv-LV": "lv", "ml-IN": "in", "mr-IN": "in", "ms-MY": "my", "ne-NP": "np",
  "nl-NL": "nl", "no-NO": "no", "or-IN": "in", "pl-PL": "pl", "pt-BR": "br",
  "pt-PT": "pt", "ro-RO": "ro", "ru-RU": "ru", "sk-SK": "sk", "sq-AL": "al",
  "sr-SP": "rs", "sv-SE": "se", "ta-IN": "in", "te-IN": "in", "th-TH": "th",
  "tr-TR": "tr", "uk-UA": "ua", "uz-UZ": "uz", "vi-VN": "vn", "zh-CN": "cn",
  "zh-TW": "tw",
};

function flagUrl(locale: string): string {
  const iso2 = LANGUAGE_FLAGS[locale] ?? "gb";
  return `https://flagcdn.com/w40/${iso2}.png`;
}

/* eslint-disable lingui/no-unlocalized-strings */
const NATIVE_NAMES: Record<string, string> = {
  "af-ZA": "Afrikaans", "am-ET": "አማርኛ", "ar-SA": "العربية", "bg-BG": "Български",
  "bn-BD": "বাংলা", "ca-ES": "Català", "cs-CZ": "Čeština", "da-DK": "Dansk",
  "de-DE": "Deutsch", "el-GR": "Ελληνικά", "en-US": "English", "es-ES": "Español",
  "fa-IR": "فارسی", "fi-FI": "Suomi", "fr-FR": "Français", "he-IL": "עברית",
  "hi-IN": "हिन्दी", "hu-HU": "Magyar", "id-ID": "Indonesia", "it-IT": "Italiano",
  "ja-JP": "日本語", "km-KH": "ខ្មែរ", "kn-IN": "ಕನ್ನಡ", "ko-KR": "한국어",
  "lt-LT": "Lietuvių", "lv-LV": "Latviešu", "ml-IN": "മലയാളം", "mr-IN": "मराठी",
  "ms-MY": "Melayu", "ne-NP": "नेपाली", "nl-NL": "Nederlands", "no-NO": "Norsk",
  "or-IN": "ଓଡ଼ିଆ", "pl-PL": "Polski", "pt-BR": "Português (BR)", "pt-PT": "Português",
  "ro-RO": "Română", "ru-RU": "Русский", "sk-SK": "Slovenčina", "sq-AL": "Shqip",
  "sr-SP": "Српски", "sv-SE": "Svenska", "ta-IN": "தமிழ்", "te-IN": "తెలుగు",
  "th-TH": "ภาษาไทย", "tr-TR": "Türkçe", "uk-UA": "Українська", "uz-UZ": "O'zbek",
  "vi-VN": "Tiếng Việt", "zh-CN": "中文 (简)", "zh-TW": "中文 (繁)",
};
/* eslint-enable lingui/no-unlocalized-strings */

function getInitials(name?: string | null, email?: string | null): string {
  if (name) {
    return name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  }
  return (email?.[0] ?? "?").toUpperCase();
}

/* eslint-disable lingui/no-unlocalized-strings */
const TIER_LABEL: Record<string, string> = {
  BASIC: "Basic",
  PROFESSIONAL: "Pro",
  ENTERPRISE: "Enterprise",
};
/* eslint-enable lingui/no-unlocalized-strings */

type Props = {
  collapsed?: boolean;
};

export const UserProfileFooter = ({ collapsed = false }: Props) => {
  const user = useAuthStore((s) => s.user);
  const { subscriptionTier } = useAuthStore();
  const { logout } = useLogout();
  const navigate = useNavigate();

  const [userPopOpen, setUserPopOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [langSearch, setLangSearch] = useState("");
  const userWrapRef = useRef<HTMLDivElement>(null);

  const currentLocale = getCurrentUrlLocale();
  const isRTL = isRTLLocale(currentLocale);
  const currentLangName =
    languages.find(
      (l) =>
        l.locale.toLowerCase() === currentLocale.toLowerCase() ||
        l.id.toLowerCase() === currentLocale.toLowerCase(),
    )?.name ?? "English";
  const currentLangCode = currentLangName.slice(0, 2).toUpperCase();
  const filteredLangs = langSearch.trim()
    ? languages.filter((l) => l.name.toLowerCase().includes(langSearch.toLowerCase()))
    : languages;

  useEffect(() => {
    if (!userPopOpen) {
      setLangOpen(false);
      setLangSearch("");
      return;
    }
    const handler = (e: MouseEvent) => {
      if (userWrapRef.current && !userWrapRef.current.contains(e.target as Node)) {
        setUserPopOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => { document.removeEventListener("mousedown", handler); };
  }, [userPopOpen]);

  const initials = getInitials(user?.name, user?.email);
  const tierLabel = subscriptionTier ? TIER_LABEL[subscriptionTier] : undefined;

  return (
    <div
      ref={userWrapRef}
      style={{
        flexShrink: 0,
        borderTop: "1px solid var(--border,#e5e5e5)",
        background: "#fff",
        padding: collapsed ? "10px 0" : "10px 12px",
        position: "relative",
      }}
    >
      {/* User popup — slides up from bottom, opens to the side */}
      {userPopOpen && (
        <div
          role="menu"
          style={{
            position: "absolute",
            ...(isRTL ? { right: "calc(100% + 8px)" } : { left: "calc(100% + 8px)" }),
            bottom: 10,
            width: 248,
            background: "#fff",
            border: "1px solid var(--border,#e5e5e5)",
            borderRadius: 12,
            boxShadow: "var(--shadow-lg, 0 12px 40px rgba(22,52,76,0.14))",
            padding: 6,
            zIndex: 9999,
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 10px 12px",
              borderBottom: "1px solid var(--border,#e5e5e5)",
              marginBottom: 6,
            }}
          >
            <span
              style={{
                width: 38,
                height: 38,
                borderRadius: "50%",
                background: "linear-gradient(135deg,var(--scv-teal-primary,#268599),var(--scv-teal-deep,#155469))",
                color: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 13,
                fontWeight: 700,
                flexShrink: 0,
                letterSpacing: "0.02em",
              }}
            >
              {initials}
            </span>
            <div style={{ lineHeight: 1.3, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "var(--fg-1,#171717)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {user?.name || user?.email}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "var(--fg-3,#737373)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {user?.email}
              </div>
            </div>
          </div>

          {/* Plan card */}
          <div
            style={{
              margin: 4,
              padding: "10px 12px",
              borderRadius: 8,
              background: "linear-gradient(135deg,rgba(38,133,153,0.08),rgba(232,136,94,0.10))",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span
              style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                background: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--scv-coral-deep,#cd7858)",
                flexShrink: 0,
              }}
            >
              <Star size={16} weight="regular" />
            </span>
            <span style={{ flex: 1, minWidth: 0, lineHeight: 1.3 }}>
              <span
                style={{ display: "block", fontSize: 12, fontWeight: 700, color: "var(--fg-1,#171717)" }}
              >
                {tierLabel ? `${tierLabel} plan` : t`Free plan`}
              </span>
              <span style={{ display: "block", fontSize: 11, color: "var(--fg-3,#737373)" }}>
                {t`Unlimited resumes & AI rewrites`}
              </span>
            </span>
            <button
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--scv-coral-deep,#cd7858)",
                padding: "5px 10px",
                borderRadius: 9999,
                background: "#fff",
                border: "1px solid rgba(232,136,94,0.30)",
                cursor: "pointer",
                flexShrink: 0,
              }}
              onClick={() => { navigate("/pricing"); setUserPopOpen(false); }}
              onMouseEnter={(e) => {
                const el = e.currentTarget;
                el.style.background = "var(--scv-coral,#e8885e)";
                el.style.color = "#fff";
                el.style.borderColor = "var(--scv-coral,#e8885e)";
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget;
                el.style.background = "#fff";
                el.style.color = "var(--scv-coral-deep,#cd7858)";
                el.style.borderColor = "rgba(232,136,94,0.30)";
              }}
            >
              {t`Manage`}
            </button>
          </div>

          {/* Separator */}
          <div style={{ height: 1, background: "var(--border,#e5e5e5)", margin: "6px 4px" }} />

          {/* Menu rows */}
          {[
            { Icon: UserCircle, label: t`My profile`, to: "/dashboard/profile" },
            { Icon: FileText, label: t`My resumes`, to: "/dashboard/resumes" },
            { Icon: CreditCard, label: t`Billing & invoices`, to: "/dashboard/transaction-history" },
            { Icon: Gear, label: t`Account settings`, to: "/dashboard/settings" },
            { Icon: Question, label: t`Help & support`, to: "/dashboard/help" },
          ].map(({ Icon, label, to }) => (
            <Link
              key={label}
              to={to}
              role="menuitem"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                width: "100%",
                padding: "8px 10px",
                borderRadius: 6,
                fontSize: 13,
                color: "var(--fg-1,#171717)",
                fontWeight: 500,
                textDecoration: "none",
                transition: "background 0.12s",
              }}
              onClick={() => setUserPopOpen(false)}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "var(--scv-gray-100,#f5f5f5)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
            >
              <Icon size={16} style={{ color: "var(--fg-3,#737373)", flexShrink: 0 }} />
              <span style={{ flex: 1 }}>{label}</span>
            </Link>
          ))}

          {/* Language section */}
          <div
            style={{
              margin: "4px 0 2px",
              borderRadius: 8,
              background: langOpen ? "var(--scv-gray-50,#f7f7f7)" : "transparent",
              border: langOpen ? "1px solid var(--border,#e5e5e5)" : "1px solid transparent",
              overflow: "hidden",
              transition: "background 0.15s, border-color 0.15s",
            }}
          >
            <button
              role="menuitem"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                width: "100%",
                padding: "8px 10px",
                borderRadius: langOpen ? 0 : 6,
                fontSize: 13,
                color: "var(--fg-1,#171717)",
                fontWeight: 500,
                background: "transparent",
                border: "none",
                cursor: "pointer",
                textAlign: "left",
                transition: "background 0.12s",
              }}
              onClick={() => setLangOpen((v) => !v)}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "var(--scv-gray-100,#f0f0f0)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
            >
              <Globe size={16} style={{ color: "var(--fg-3,#737373)", flexShrink: 0 }} />
              <span style={{ flex: 1 }}>{t`Language`}</span>
              <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <img
                  src={flagUrl(currentLocale)}
                  alt={currentLangName}
                  style={{ width: 16, height: 11, objectFit: "cover", borderRadius: 2, flexShrink: 0 }}
                />
                <span
                  style={{
                    fontSize: 10,
                    color: langOpen ? "var(--accent,#268599)" : "var(--fg-4,#a3a3a3)",
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                  }}
                >
                  {currentLangCode}
                </span>
              </span>
            </button>

            {langOpen && (
              <div style={{ borderTop: "1px solid var(--border,#e5e5e5)" }}>
                <div
                  style={{
                    position: "relative",
                    padding: "6px 8px",
                    borderBottom: "1px solid var(--border,#e5e5e5)",
                  }}
                >
                  <MagnifyingGlass
                    size={12}
                    style={{
                      position: "absolute",
                      left: 16,
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "var(--fg-4,#a3a3a3)",
                      pointerEvents: "none",
                    }}
                  />
                  <input
                    autoFocus
                    value={langSearch}
                    placeholder={t`Search languages…`}
                    style={{
                      width: "100%",
                      height: 28,
                      paddingLeft: 24,
                      paddingRight: 8,
                      border: "1px solid var(--border,#e5e5e5)",
                      borderRadius: 4,
                      fontSize: 12,
                      color: "var(--fg-1,#171717)",
                      background: "#fff",
                      outline: "none",
                      boxSizing: "border-box",
                      transition: "border-color 0.1s",
                    }}
                    onChange={(e) => setLangSearch(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    onFocus={(e) => { e.currentTarget.style.borderColor = "var(--accent,#268599)"; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = "var(--border,#e5e5e5)"; }}
                  />
                </div>
                <div style={{ maxHeight: 180, overflowY: "auto" }}>
                  {filteredLangs.map((lang) => {
                    const isActive =
                      lang.locale.toLowerCase() === currentLocale.toLowerCase() ||
                      lang.id.toLowerCase() === currentLocale.toLowerCase();
                    return (
                      <button
                        key={lang.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          width: "100%",
                          padding: "6px 10px",
                          border: "none",
                          background: "transparent",
                          fontSize: 12,
                          color: isActive ? "var(--accent,#268599)" : "var(--fg-1,#171717)",
                          fontWeight: isActive ? 600 : 400,
                          cursor: "pointer",
                          textAlign: "left",
                          transition: "background 0.1s",
                        }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "var(--scv-gray-100,#ebebeb)"; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
                        onClick={() => { void changeLanguage(lang.locale); setUserPopOpen(false); }}
                      >
                        <img
                          src={flagUrl(lang.locale)}
                          alt={lang.name}
                          style={{ width: 16, height: 11, objectFit: "cover", borderRadius: 2, flexShrink: 0 }}
                        />
                        <span style={{ flex: 1, lineHeight: 1.3 }}>
                          {lang.name}
                          {NATIVE_NAMES[lang.locale] && NATIVE_NAMES[lang.locale] !== lang.name && (
                            <span
                              style={{
                                marginLeft: 4,
                                fontSize: 11,
                                color: isActive ? "var(--accent,#268599)" : "var(--fg-4,#a3a3a3)",
                                fontWeight: 400,
                              }}
                            >
                              ({NATIVE_NAMES[lang.locale]})
                            </span>
                          )}
                        </span>
                        {isActive && (
                          <Check size={12} style={{ color: "var(--accent,#268599)", flexShrink: 0 }} />
                        )}
                      </button>
                    );
                  })}
                  {filteredLangs.length === 0 && (
                    <p style={{ padding: "10px 12px", fontSize: 12, color: "var(--fg-4,#a3a3a3)", textAlign: "center" }}>
                      {t`No languages match`}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Separator */}
          <div style={{ height: 1, background: "var(--border,#e5e5e5)", margin: "6px 4px" }} />

          {/* Sign out */}
          <button
            role="menuitem"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              width: "100%",
              padding: "8px 10px",
              borderRadius: 6,
              fontSize: 13,
              color: "var(--scv-coral-deep,#cd7858)",
              fontWeight: 500,
              background: "none",
              border: "none",
              cursor: "pointer",
              textAlign: "left",
              transition: "background 0.12s",
            }}
            onClick={() => { logout(); setUserPopOpen(false); }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "rgba(232,136,94,0.08)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
          >
            <SignOut size={16} style={{ color: "var(--scv-coral-deep,#cd7858)", flexShrink: 0 }} />
            {t`Sign out`}
          </button>
        </div>
      )}

      {/* Trigger button */}
      <button
        aria-haspopup="menu"
        aria-expanded={userPopOpen}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : undefined,
          gap: collapsed ? 0 : 10,
          padding: collapsed ? "8px 0" : "8px 8px",
          borderRadius: 8,
          background: userPopOpen ? "var(--scv-gray-100,#f5f5f5)" : "transparent",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
          transition: "background 0.15s",
        }}
        onClick={() => setUserPopOpen((v) => !v)}
        onMouseEnter={(e) => {
          if (!userPopOpen) (e.currentTarget as HTMLElement).style.background = "var(--scv-gray-100,#f5f5f5)";
        }}
        onMouseLeave={(e) => {
          if (!userPopOpen) (e.currentTarget as HTMLElement).style.background = "transparent";
        }}
      >
        {/* Avatar with online dot */}
        <span style={{ position: "relative", flexShrink: 0 }}>
          <span
            style={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              background: "linear-gradient(135deg,var(--scv-teal-primary,#268599),var(--scv-teal-deep,#155469))",
              color: "#fff",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.02em",
            }}
          >
            {initials}
          </span>
          <span
            style={{
              position: "absolute",
              right: -1,
              bottom: -1,
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: "var(--scv-success,#22c55e)",
              boxShadow: "0 0 0 2px #fff",
            }}
          />
        </span>

        {/* Name + email */}
        {!collapsed && (
          <span style={{ flex: 1, minWidth: 0, lineHeight: 1.25 }}>
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 13,
                fontWeight: 600,
                color: "var(--fg-1,#171717)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
                {user?.name ?? user?.email ?? t`Account`}
              </span>
              {tierLabel && (
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: "var(--scv-coral-deep,#cd7858)",
                    background: "rgba(232,136,94,0.12)",
                    padding: "1px 6px",
                    borderRadius: 4,
                    flexShrink: 0,
                  }}
                >
                  {tierLabel}
                </span>
              )}
            </span>
            <span
              style={{
                display: "block",
                fontSize: 11,
                color: "var(--fg-3,#737373)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {user?.email}
            </span>
          </span>
        )}

        {/* Caret */}
        {!collapsed && (
          <CaretUp
            size={14}
            style={{
              color: userPopOpen ? "var(--accent,#268599)" : "var(--fg-3,#737373)",
              flexShrink: 0,
              transition: "transform 0.15s",
              transform: userPopOpen ? "rotate(0deg)" : "rotate(180deg)",
            }}
          />
        )}
      </button>
    </div>
  );
};
